/**
 * One column of the all-regions day strip (BON-534).
 *
 * Its own file because the strip outgrew the size cap once the design's cell
 * metrics went in, and because a day cell is a thing in its own right.
 *
 * The two viewports arrange it differently, and BOTH come from the design.
 * Wide: the weekday and the date share a baseline row with the count beneath.
 * Phone: weekday, then count, then date, in one centred column — at seven
 * columns across 390px there is no room for a row, and the count is what the
 * eye should land on.
 */
import type { OverviewDayCount } from './overview-types';

/** The metrics the strip hands down, so a cell never decides its own size. */
export interface WeekStripDaySize {
  pad: string;
  align: string;
  weekday: number;
  count: number;
  date: number;
  minHeight: number;
}

export interface WeekStripDayProps {
  day: OverviewDayCount;
  /** Already resolved by the strip — cells do no date maths. */
  weekday: string;
  shortDate: string;
  isBusiest: boolean;
  /** Phone widths: the weekday drops to a single letter. */
  compact: boolean;
  size: WeekStripDaySize;
}

const WEEKDAY = (size: WeekStripDaySize) =>
  ({
    fontSize: size.weekday,
    fontWeight: 700,
    letterSpacing: '.05em',
    textTransform: 'uppercase',
    color: 'var(--nb-subtle)',
  }) as const;

const COUNT = (size: WeekStripDaySize, isBusiest: boolean) =>
  ({
    fontSize: size.count,
    fontWeight: 700,
    lineHeight: 1,
    color: isBusiest ? 'var(--nb-primary)' : 'var(--nb-bark-dark)',
  }) as const;

const DATE = (size: WeekStripDaySize) =>
  ({ fontSize: size.date, color: 'var(--nb-faint)', whiteSpace: 'nowrap' }) as const;

export function WeekStripDay({
  day,
  weekday,
  shortDate,
  isBusiest,
  compact,
  size,
}: WeekStripDayProps) {
  return (
    <div
      data-testid={`week-strip-day-${day.date}`}
      data-busiest={isBusiest}
      style={{
        background: day.workshopCount ? 'var(--nb-surface)' : 'transparent',
        border: '1px solid var(--nb-border)',
        borderRadius: 8,
        padding: size.pad,
        minHeight: size.minHeight,
        display: 'flex',
        flexDirection: 'column',
        alignItems: size.align,
        gap: 4,
        minWidth: 0,
      }}
    >
      {compact ? (
        <>
          <span style={WEEKDAY(size)}>{weekday.charAt(0)}</span>
          <span style={COUNT(size, isBusiest)}>{day.workshopCount || '—'}</span>
          <span style={DATE(size)}>{shortDate}</span>
        </>
      ) : (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: 4,
              alignSelf: 'stretch',
            }}
          >
            <span style={WEEKDAY(size)}>{weekday}</span>
            <span className="nb-tabular nb-mono" style={DATE(size)}>
              {shortDate}
            </span>
          </div>
          <span style={COUNT(size, isBusiest)}>{day.workshopCount || '—'}</span>
        </>
      )}
    </div>
  );
}
